// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useRegistrarDelegatedBurn } from '@hooks/mutations/registry/burn/useRegistrarDelegatedBurn';
import { useGetPrepareHoldingsWithLocks } from '@hooks/other/useGetPrepareHoldingsWithLocks';
import { useCallback, useState } from 'react';
import { Holding } from '@daml.js/dtc-registry-holding/lib/Dtc/Registry/Holding/V3/Holding';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useLimits } from '../../../contexts/LimitsContext';
import { useRegistrarHoldingOperationsDelegationsByParty } from '../../../hooks/queries/registry/registrar/useRegistrarHoldingOperationsDelegationsByParty';
import { useInstrumentConfigurationsById } from '../../../hooks/queries/useGetAllInstrumentConfigurations';
import TemplateContract from '../../../utils/TemplateContract';
import { validateBurnAmount } from '../../../utils/getValidateAmountMessage';
import { MaxLimitErrorMessage } from '../transfer/MaxLimitErrorMessage';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  holding?: TemplateContract<Holding>;
  disableDialog?: boolean;
};

export const RegistrarBurnDialog = ({ disableDialog, open, onClose, holding }: DialogProps) => {
  const [tokenId, setTokenId] = useState<string>(holding?.payload.instrument.id || '');
  const [targetParty, setTargetParty] = useState<string>('');
  const [amount, setAmount] = useState<string>(holding?.payload.amount || '');
  const [label, setLabel] = useState<string>(holding?.payload.label || '');
  const [reference, setReference] = useState<string>('');

  const [showMaxLimitError, setShowMaxLimitError] = useState(false);

  const { burnLimit } = useLimits();
  const instruments = useInstrumentConfigurationsById();
  const holdingRightsDelegations = useRegistrarHoldingOperationsDelegationsByParty();

  const registrarDelegatedBurn = useRegistrarDelegatedBurn();
  const prepareHoldings = useGetPrepareHoldingsWithLocks();

  const resetState = () => {
    setTokenId('');
    setTargetParty('');
    setAmount('');
    setLabel('');
    setReference('');
  };

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose]);

  const burnAmountValidation = validateBurnAmount(burnLimit, holding?.payload.amount)(amount);

  const doBurn = async () => {
    const instrument = instruments[tokenId];

    if (!instrument) {
      // This should never happen, since the button is disabled if the instrument
      // cannot be found among the known configurations.
      throw Error(`Cannot find instrument configuration for token: ${tokenId}`);
    }

    const delegation = holdingRightsDelegations[targetParty];

    if (!delegation) {
      // This should never happen, since the button is disabled if the instrument
      // cannot be found among the known configurations.
      throw Error(`Cannot find delegation for token: ${targetParty}`);
    }

    const instrumentId = instrument.payload.defaultIdentifier.id;
    const registrar = instrument.payload.registrar;

    const { holdingCids, transferOfferCids, allocationCids, exceedsMaxLimit } = prepareHoldings({
      instrumentId,
      registrar,
      owner: targetParty,
    });
    setShowMaxLimitError(false);
    if (exceedsMaxLimit) {
      setShowMaxLimitError(true);
      return;
    }

    await registrarDelegatedBurn({
      party: targetParty,
      amount,
      source: registrar,
      instrumentId,
      holdingCids,
      reference,
      delegationCid: delegation.contractId,
      transferOfferCids,
      allocationCids,
    });
    handleClose();
  };

  const disableSubmit =
    !burnAmountValidation.isValid ||
    !amount ||
    !tokenId ||
    !instruments[tokenId] ||
    !targetParty ||
    !holdingRightsDelegations[targetParty];

  return (
    <FormDialog
      disableDialog={disableDialog}
      open={open}
      title="Burn"
      onCancel={handleClose}
      onSubmit={doBurn}
      submitName="Burn"
      isSubmitDisabled={disableSubmit}
    >
      <TextInput
        enableTopLabel
        label="Instrument ID"
        value={tokenId}
        setValue={setTokenId}
        required
      />
      <TextInput
        enableTopLabel
        label="Wallet"
        value={targetParty}
        setValue={setTargetParty}
        required
      />
      <TextInput
        enableTopLabel
        type="number"
        validationMessage={burnAmountValidation.message}
        label="Amount"
        value={amount}
        setValue={setAmount}
        inputProps={{ step: 'any' }}
        required
      />
      <TextInput
        enableTopLabel
        label="Label"
        value={label}
        setValue={setLabel}
        disabled={!!holding}
        hidden
      />

      {showMaxLimitError && <MaxLimitErrorMessage />}

      <TextInput enableTopLabel label="Reference" value={reference} setValue={setReference} />
    </FormDialog>
  );
};
